import PageHeader from "@/components/common/PageHeader";
import { monitoringService } from "@/services/monitoring.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Link, useParams } from "react-router-dom";

const STATUS_BADGE: Record<string, string> = {
  open: "bg-red-50 text-red-600",
  regressed: "bg-orange-50 text-orange-600",
  resolved: "bg-green-50 text-green-600",
  ignored: "bg-slate-100 text-slate-500",
};

export default function ErrorDetailPage() {
  const { fingerprint } = useParams();
  const queryClient = useQueryClient();

  const { data: error, isLoading } = useQuery({
    queryKey: ["error-detail", fingerprint],
    queryFn: () => monitoringService.getErrorDetail(fingerprint!),
    enabled: Boolean(fingerprint),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => monitoringService.resolveError(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["error-detail"] });
      toast.success("Error resolved");
    },
  });

  const ignoreMutation = useMutation({
    mutationFn: (id: string) => monitoringService.ignoreError(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["error-detail"] });
      toast.success("Error ignored");
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => monitoringService.reopenError(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["error-detail"] });
      toast.success("Error reopened");
    },
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3" />
          <div className="h-64 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (!error) {
    return (
      <div className="p-6">
        <PageHeader
          title="Error Not Found"
          subtitle="The error with this fingerprint could not be found"
        />
        <Link
          to="/monitoring/errors"
          className="text-blue-600 hover:underline text-sm"
        >
          ← Back to Error Tracking
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title={error.message || "Error Detail"}
          subtitle={`Fingerprint: ${fingerprint}`}
        />
        <Link
          to="/monitoring/errors"
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          ← Back to Errors
        </Link>
      </div>

      {/* Status & Actions */}
      <div className="flex items-center gap-4">
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${STATUS_BADGE[error.status] || "bg-slate-100 text-slate-500"}`}
        >
          {error.status?.toUpperCase()}
        </span>
        <span className="text-xs text-slate-500">
          Platform: <strong>{error.platform}</strong>
        </span>
        <span className="text-xs text-slate-500">
          Type: <strong>{error.errorType}</strong>
        </span>
        <div className="ml-auto flex gap-2">
          {error.status === "open" && (
            <>
              <button
                onClick={() => resolveMutation.mutate(error.id)}
                className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700"
              >
                Resolve
              </button>
              <button
                onClick={() => ignoreMutation.mutate(error.id)}
                className="rounded-lg bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-300"
              >
                Ignore
              </button>
            </>
          )}
          {(error.status === "resolved" || error.status === "ignored") && (
            <button
              onClick={() => reopenMutation.mutate(error.id)}
              className="rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-700"
            >
              Reopen
            </button>
          )}
        </div>
      </div>

      {/* Error Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Stats */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
            Statistics
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Occurrences</span>
              <span className="text-sm font-bold text-slate-900">
                {error.occurrenceCount?.toLocaleString() || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Affected Users</span>
              <span className="text-sm font-bold text-slate-900">
                {error.affectedUsers?.toLocaleString() || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">First Seen</span>
              <span className="text-sm text-slate-900">
                {error.firstSeenAt
                  ? new Date(error.firstSeenAt).toLocaleDateString()
                  : "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Last Seen</span>
              <span className="text-sm text-slate-900">
                {error.lastSeenAt
                  ? new Date(error.lastSeenAt).toLocaleDateString()
                  : "—"}
              </span>
            </div>
          </div>
        </div>

        {/* Tenant Info */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
            Tenant
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Name</span>
              <Link
                to={`/tenants/${error.tenantId}`}
                className="text-sm font-semibold text-blue-600 hover:underline"
              >
                {error.tenant?.name || error.tenantId}
              </Link>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Email</span>
              <span className="text-sm text-slate-900">
                {error.tenant?.email || "—"}
              </span>
            </div>
            {error.source && (
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Source</span>
                <span className="text-sm text-slate-900 font-mono text-xs">
                  {error.source}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Resolution Info */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
            Resolution
          </h3>
          <div className="space-y-3">
            {error.resolvedAt && (
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Resolved At</span>
                <span className="text-sm text-slate-900">
                  {new Date(error.resolvedAt).toLocaleString()}
                </span>
              </div>
            )}
            {error.resolvedNote && (
              <div>
                <span className="text-sm text-slate-600">Notes</span>
                <p className="text-sm text-slate-900 mt-1 bg-slate-50 rounded-lg p-2">
                  {error.resolvedNote}
                </p>
              </div>
            )}
            {error.assignedTo && (
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Assigned To</span>
                <span className="text-sm text-slate-900">
                  {error.assignedTo}
                </span>
              </div>
            )}
            {!error.resolvedAt && !error.resolvedNote && !error.assignedTo && (
              <p className="text-sm text-slate-400 italic">Not yet resolved</p>
            )}
          </div>
        </div>
      </div>

      {/* Stack Trace */}
      {error.stackTrace && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
            Stack Trace
          </h3>
          <pre className="bg-slate-900 text-green-400 p-4 rounded-xl text-xs overflow-x-auto max-h-96 overflow-y-auto font-mono leading-relaxed">
            {error.stackTrace}
          </pre>
        </div>
      )}

      {/* Metadata */}
      {error.metadata && Object.keys(error.metadata).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
            Metadata
          </h3>
          <pre className="bg-slate-50 p-4 rounded-xl text-xs overflow-x-auto text-slate-700 font-mono">
            {JSON.stringify(error.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
